<template>
  <v-chart ref="chartRef" class="chart" :option="option" />
</template>

<script setup>
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { GraphChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';
import { computed, defineProps, ref, onMounted, onBeforeUnmount } from 'vue';

// Register echarts modules
use([
  CanvasRenderer,
  GraphChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
]);

// ---------- Props ----------
const props = defineProps({
  networkData: {
    type: Object,
    default: () => ({
      nodes: [],
      links: [],
      categories: []
    })
  },
  title: {
    type: String,
    default: 'Clinical–Protein Correlation Network'
  }
});

// ---------- Helper ----------
function truncate(name, length = 20) {
  return name.length > length ? name.slice(0, length) + '…' : name;
}

// ---------- ECharts Option ----------
const option = computed(() => {
  const { nodes = [], links = [], categories = [] } = props.networkData;
  
  if (nodes.length === 0) {
    return {
      title: {
        text: 'No data available',
        left: 'center',
        top: 'center',
        textStyle: { color: '#999', fontSize: 16 }
      }
    };
  }

  return {
    backgroundColor: '#ffffff',
    legend: [
      {
        type: 'scroll',
        orient: 'vertical',
        height: '80%',
        top: 'middle',
        right: 50,
        data: categories.map(c => c.name),
        tooltip: { show: true },
        formatter(name) {
          return truncate(name, 20);
        },
        textStyle: {
          color: '#333',
          fontSize: 13,
        },
      },
    ],
    tooltip: {
      trigger: 'item',
      formatter: (p) => {
        if (p.dataType === 'node') {
          const parts = [`<b>${p.data.name}</b>`, `Category: ${p.data.category}`];
          if (p.data.value !== undefined && p.data.value !== null) {
            parts.push(`AverageValue: ${typeof p.data.value === 'number' ? p.data.value.toFixed(2) : p.data.value}`);
          }
          return parts.join('<br/>');
        }
        if (p.dataType === 'edge') {
          const sourceName = nodes.find(n => n.id === p.data.source)?.name || p.data.source;
          const targetName = nodes.find(n => n.id === p.data.target)?.name || p.data.target;
          return `<b>${sourceName}</b> ↔ <b>${targetName}</b><br/>Correlation: ${p.data.value?.toFixed(3) || 'N/A'}`;
        }
        return '';
      },
    },
    series: [
      {
        type: 'graph',
        layout: 'circular',
        circular: { rotateLabel: true },
        data: nodes,
        links: links,
        roam: true,
        focusNodeAdjacency: true,
        categories: categories,
        label: {
          show: true,
          fontSize: 12,
          color: '#333',
        },
        lineStyle: { curveness: 0.25 },
        emphasis: { focus: 'adjacency' },
      },
    ],
  };
});

// ---------- Resize handling (avoid ResizeObserver loop) ----------
const chartRef = ref(null);

function handleResize() {
  // Defer resize to next frame to avoid running inside ResizeObserver delivery
  window.requestAnimationFrame(() => {
    if (chartRef.value && typeof chartRef.value.resize === 'function') {
      chartRef.value.resize();
    }
  });
}

onMounted(() => {
  window.addEventListener('resize', handleResize, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
.chart {
  height: 100%;
  width: 100%;
  background: #fff;
  position: relative;
  overflow: hidden;
}
</style>

